import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { CircularProgress, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useICCCColors } from "../components/theme";
import DeviceMarker from "./DeviceMarker";

const MIN_SCALE = 0.5;
const MAX_SCALE = 4;
const clampScale = (s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

export default function MapCanvas({
  backgroundUrl,
  devices,
  editable,
  scale,
  translate,
  onViewChange,
  onMarkerClick,
  onMarkerDragEnd,
  onUnplacedDrop,
}) {
  const ICCC_COLORS = useICCCColors();
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageBroken, setImageBroken] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const panState = useRef(null);

  // MapCanvas isn't remounted when the selected location's tab changes, so
  // the loaded/broken flags from the previous map must be cleared explicitly
  // — otherwise a broken-image overlay can persist over a valid next map, or
  // the loading spinner never shows for it.
  useEffect(() => {
    setImageLoaded(false);
    setImageBroken(false);
  }, [backgroundUrl]);

  // React 17 attaches wheel listeners at the root as passive, so a JSX
  // onWheel's e.preventDefault() is silently ignored — the page would scroll
  // underneath the canvas instead of only zooming it. A native listener with
  // { passive: false } is required to actually suppress page scroll.
  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return undefined;
    const onWheel = (e) => {
      if (!editable) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      onViewChange({ scale: clampScale(scale + delta), translate });
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [scale, translate, onViewChange, editable]);

  // Pan (and zoom) are Edit-mode only: in view mode the map stays filling the
  // container, and dragging never knocks it off position.
  const handlePointerDown = (e) => {
    if (!editable) return;
    setIsPanning(true);
    panState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: translate.x,
      originY: translate.y,
    };
    viewportRef.current?.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!panState.current) return;
    const dx = e.clientX - panState.current.startX;
    const dy = e.clientY - panState.current.startY;
    onViewChange({
      scale,
      translate: { x: panState.current.originX + dx, y: panState.current.originY + dy },
    });
  };

  const handlePointerUp = (e) => {
    panState.current = null;
    setIsPanning(false);
    viewportRef.current?.releasePointerCapture(e.pointerId);
  };

  const handleDragOver = (e) => {
    if (!editable) return;
    e.preventDefault();
  };

  const handleDrop = (e) => {
    if (!editable) return;
    e.preventDefault();
    const assetId = e.dataTransfer.getData("text/plain");
    if (!assetId || !contentRef.current) return;
    const box = contentRef.current.getBoundingClientRect();
    const xPct = ((e.clientX - box.left) / box.width) * 100;
    const yPct = ((e.clientY - box.top) / box.height) * 100;
    onUnplacedDrop(assetId, Math.min(100, Math.max(0, xPct)), Math.min(100, Math.max(0, yPct)));
  };

  return (
    <MDBox
      ref={viewportRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        height: "100%",
        bgcolor: ICCC_COLORS.cardBg2,
        border: `1px solid ${ICCC_COLORS.cardBorder}`,
        borderRadius: 1.5,
        cursor: editable ? (isPanning ? "grabbing" : "grab") : "default",
        touchAction: "none",
      }}
    >
      {!backgroundUrl && (
        <MDBox
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Icon sx={{ color: ICCC_COLORS.textMuted, fontSize: 40 }}>image_not_supported</Icon>
          <MDTypography variant="body2" sx={{ color: ICCC_COLORS.textMuted }}>
            This location has no Background Image URL set. Add one in Location Master.
          </MDTypography>
        </MDBox>
      )}

      {backgroundUrl && !imageLoaded && !imageBroken && (
        <MDBox
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={28} sx={{ color: ICCC_COLORS.accent }} />
        </MDBox>
      )}

      {backgroundUrl && imageBroken && (
        <MDBox
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Icon sx={{ color: ICCC_COLORS.critical, fontSize: 40 }}>broken_image</Icon>
          <MDTypography variant="body2" sx={{ color: ICCC_COLORS.textMuted }}>
            Unable to load the map image for this location.
          </MDTypography>
        </MDBox>
      )}

      {backgroundUrl && !imageBroken && (
        <MDBox
          ref={contentRef}
          sx={{
            // The image is a true background fill: it stretches to the whole
            // square container (100% 100%), so the full map is always visible
            // edge-to-edge with no letterbox bands and no cropping.
            position: "absolute",
            inset: 0,
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transformOrigin: "center",
            opacity: imageLoaded ? 1 : 0,
            backgroundImage: `url(${backgroundUrl})`,
            backgroundSize: "100% 100%",
          }}
        >
          <img
            src={backgroundUrl}
            alt=""
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageBroken(true)}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0 }}
          />
          {devices
            .filter((d) => d.xCoordinate != null && d.yCoordinate != null)
            .map((d) => (
              <DeviceMarker
                key={d.id}
                device={d}
                editable={editable}
                scale={scale}
                onClick={onMarkerClick}
                onDragEnd={onMarkerDragEnd}
              />
            ))}
        </MDBox>
      )}
    </MDBox>
  );
}

MapCanvas.propTypes = {
  backgroundUrl: PropTypes.string,
  devices: PropTypes.arrayOf(PropTypes.object).isRequired,
  editable: PropTypes.bool,
  scale: PropTypes.number.isRequired,
  translate: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }).isRequired,
  onViewChange: PropTypes.func.isRequired,
  onMarkerClick: PropTypes.func,
  onMarkerDragEnd: PropTypes.func,
  onUnplacedDrop: PropTypes.func,
};

MapCanvas.defaultProps = {
  backgroundUrl: null,
  editable: false,
  onMarkerClick: () => {},
  onMarkerDragEnd: () => {},
  onUnplacedDrop: () => {},
};
